import { useRef, useState } from 'react';
import axios from 'axios';
import { Delete as DeleteIcon, CloudUpload, InsertDriveFile } from '@mui/icons-material';
import { API } from 'app/configs/api';
import { useTranslation } from 'react-i18next';
import useMessage from 'src/app/hooks/useMessage';

export default function UploaderFile({ accept, setValue, name, defaultValue, isPanorama = false }) {
  const [previewUrl, setPreviewUrl] = useState(defaultValue?.previewUrl || '');
  const [fileName, setFileName] = useState(defaultValue?.fileName || '');
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const { t } = useTranslation('translations');
  const { showMessage } = useMessage();

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      showMessage({
        message: t('FileTooLarge'),
        variant: 'error',
        autoHideDuration: 3000,
      });
      return;
    }

    const localUrl = URL.createObjectURL(file);
    const image = new Image();
    image.src = localUrl;
    image.onload = async () => {
      const ratio = image.width / image.height;

      if (isPanorama && ratio < 2) {
        showMessage({
          message: t('PanoramaRequired'),
          variant: 'error',
          autoHideDuration: 4000,
        });
        if (inputRef.current) inputRef.current.value = '';
        return;
      }

      if (!isPanorama && ratio >= 2) {
        showMessage({
          message: t('PanoramaNotAllowed'),
          variant: 'error',
          autoHideDuration: 4000,
        });
        if (inputRef.current) inputRef.current.value = '';
        return;
      }

      setPreviewUrl(localUrl);
      setFileName(file.name);
      setLoading(true);

      const form = new FormData();
      form.append('file', file);

      try {
        const { data } = await axios.post(API.uploadFile, form);
        setValue(name, data, { shouldValidate: true });
        showMessage({
          message: t('FileUploadSuccess'),
          variant: 'success',
          autoHideDuration: 2000,
        });
      } catch (err) {
        console.error(err);
        showMessage({
          message: t('FileUploadError'),
          variant: 'error',
          autoHideDuration: 3000,
        });
        setPreviewUrl('');
        setFileName('');
        setValue(name, null, { shouldValidate: true });
      } finally {
        setLoading(false);
      }
    };
  };

  const handleDelete = () => {
    setPreviewUrl('');
    setFileName('');
    setValue(name, null, { shouldValidate: true });
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div className="flex flex-col gap-2 mb-16">
      {previewUrl ? (
        <div className="relative flex items-center justify-between w-full p-4 border rounded-lg bg-gray-50 dark:bg-transparent">
          <div className="flex items-center gap-4 overflow-hidden">
            {fileName.match(/\.(jpe?g|png|gif|webp)$/i) ? (
              <img
                src={previewUrl}
                alt="preview"
                style={{
                  width: '60px',
                  height: '60px',
                  objectFit: 'cover',
                  borderRadius: '6px',
                  border: '1px solid #ccc',
                  flexShrink: 0,
                }}
              />
            ) : (
              <InsertDriveFile style={{ fontSize: 48, color: '#6b7280', flexShrink: 0 }} />
            )}
            <div
              style={{
                fontSize: '14px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              title={fileName}
            >
              {fileName}
            </div>
          </div>
          <button
            type="button"
            onClick={handleDelete}
            disabled={loading}
            aria-label={t('Delete')}
            className={`text-red-500 hover:text-red-700 ${
              loading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}
          >
            <DeleteIcon />
          </button>
        </div>
      ) : (
        <div
          className="flex flex-col items-center justify-center w-full p-6 border rounded-lg cursor-pointer hover:bg-gray-100"
          onClick={() => inputRef.current && inputRef.current.click()}
          style={{
            backgroundColor: '#f3f4f6',
            borderRadius: '8px',
            textAlign: 'center',
            userSelect: 'none',
          }}
        >
          <CloudUpload style={{ fontSize: 40, color: '#6b7280', marginBottom: '8px' }} />
          <p style={{ fontSize: '14px', color: '#111827' }}>{t('DragHereOrPressToChoose')}</p>

          <input
            ref={inputRef}
            type="file"
            accept={accept}
            onChange={handleUpload}
            style={{ display: 'none' }}
          />
          {loading && <p className="mt-1 text-sm text-blue-500">{t('Uploading')}</p>}
        </div>
      )}
    </div>
  );
}
